/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { InfoPopover, setupPopovers } from "components/InfoPopover"
import InputError from "components/InputError"
import { UserParameters } from "features/userParameters/UserParametersSchema"
import { FC, useEffect } from "react"
import { useFormContext } from "react-hook-form"

export enum AssetsType {
  Initial = "initial",
  Final = "final",
}

interface Props {
  legend: string
  assetsType: AssetsType
}

const Assets: FC<Props> = ({ legend, assetsType }) => {
  const {
    register,
    trigger,
    formState: { errors },
  } = useFormContext<UserParameters>()

  useEffect(() => {
    setupPopovers()
  }, [])

  const assetsStr = assetsType === AssetsType.Initial ? "initialAssets" : "finalAssets"
  const assetErrors = errors[assetsStr]

  // Helper function to determine if there are any field-specific allocation errors
  const hasFieldSpecificAllocationErrors = () => {
    if (!assetErrors?.allocation) return false

    // Check if any individual allocation fields have errors
    return !!(
      assetErrors.allocation.spxTrPct ??
      assetErrors.allocation.worldExUsEquitiesPct ??
      assetErrors.allocation.cashPct ??
      assetErrors.allocation.bm10yPct ??
      assetErrors.allocation.bm30yPct ??
      assetErrors.allocation.commoditiesPct ??
      assetErrors.allocation.goldPct
    )
  }

  // Should highlight all fields when there's a collective error (sum ≠ 100%) but no field-specific errors
  const highlightAllFields = assetErrors?.allocation && !hasFieldSpecificAllocationErrors()

  return (
    <fieldset className="form-group mb-3 p-3 border rounded" id={`${assetsType}-assets-fieldset`}>
      <legend
        id={`${assetsType}-assets-legend`}
        className={assetErrors?.allocation ? "is-invalid" : ""}
      >
        {legend}
        {assetsType === AssetsType.Final && (
          <InfoPopover content="Your assets after the glide path has ended." />
        )}
      </legend>
      <InputError error={assetErrors?.allocation} />
      <div className="row">
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`${assetsType}-expense-ratio-per-annum-pct`} className="col-form-label">
            Expense Ratio
            <InfoPopover content="The weighted average proportional per-annum expense ratio of your investment portfolio. Include ETF and mutual fund expense ratios here. Also, add AUM investment advisor fee if applicable." />
          </label>
          <div className="input-group">
            <input
              type="string"
              inputMode="decimal"
              className={`form-control expense-ratio-input${assetErrors?.expenseRatioPerAnnumPct ? " is-invalid" : ""}`}
              id={`${assetsType}-expense-ratio-per-annum-pct`}
              aria-invalid={!!assetErrors?.expenseRatioPerAnnumPct}
              {...register(`${assetsStr}.expenseRatioPerAnnumPct`)}
            />
            <span className="input-group-text">%</span>
            <InputError error={assetErrors?.expenseRatioPerAnnumPct} />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`${assetsType}-spx-tr-pct`} className="col-form-label">
            Stocks: S&P 500
          </label>
          <div className="input-group">
            <input
              type="string"
              inputMode="decimal"
              className={`form-control${assetErrors?.allocation?.spxTrPct || highlightAllFields ? " is-invalid" : ""}`}
              id={`${assetsType}-spx-tr-pct`}
              aria-invalid={!!(assetErrors?.allocation?.spxTrPct ?? highlightAllFields)}
              {...register(`${assetsStr}.allocation.spxTrPct`, {
                onBlur: () => {
                  void trigger(`${assetsStr}.allocation`)
                },
              })}
            />
            <span className="input-group-text">%</span>
            <InputError error={assetErrors?.allocation?.spxTrPct} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`${assetsType}-world-ex-us-equities-pct`} className="col-form-label">
            Stocks: Non-US
            <InfoPopover content="1970 - current" />
          </label>
          <div className="input-group">
            <input
              type="string"
              className={`form-control${assetErrors?.allocation?.worldExUsEquitiesPct || highlightAllFields ? " is-invalid" : ""}`}
              id={`${assetsType}-world-ex-us-equities-pct`}
              aria-invalid={!!(assetErrors?.allocation?.worldExUsEquitiesPct ?? highlightAllFields)}
              {...register(`${assetsStr}.allocation.worldExUsEquitiesPct`, {
                onBlur: () => {
                  void trigger(`${assetsStr}.allocation`)
                },
              })}
            />
            <span className="input-group-text">%</span>
            <InputError error={assetErrors?.allocation?.worldExUsEquitiesPct} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`${assetsType}-cash-pct`} className="col-form-label">
            Cash: 3m U.S. T-bills
          </label>
          <div className="input-group">
            <input
              type="string"
              inputMode="decimal"
              className={`form-control${assetErrors?.allocation?.cashPct || highlightAllFields ? " is-invalid" : ""}`}
              id={`${assetsType}-cash-pct`}
              aria-invalid={!!(assetErrors?.allocation?.cashPct ?? highlightAllFields)}
              {...register(`${assetsStr}.allocation.cashPct`, {
                onBlur: () => {
                  void trigger(`${assetsStr}.allocation`)
                },
              })}
            />
            <span className="input-group-text">%</span>
            <InputError error={assetErrors?.allocation?.cashPct} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`${assetsType}-bm-10y-pct`} className="col-form-label">
            Bonds: 10y U.S. Treasury
          </label>
          <div className="input-group">
            <input
              type="string"
              inputMode="decimal"
              className={`form-control${assetErrors?.allocation?.bm10yPct || highlightAllFields ? " is-invalid" : ""}`}
              id={`${assetsType}-bm-10y-pct`}
              aria-invalid={!!(assetErrors?.allocation?.bm10yPct ?? highlightAllFields)}
              {...register(`${assetsStr}.allocation.bm10yPct`, {
                onBlur: () => {
                  void trigger(`${assetsStr}.allocation`)
                },
              })}
            />
            <span className="input-group-text">%</span>
            <InputError error={assetErrors?.allocation?.bm10yPct} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`${assetsType}-bm-30y-pct`} className="col-form-label">
            Bonds: 30y U.S. Treasury
          </label>
          <div className="input-group">
            <input
              type="string"
              inputMode="decimal"
              className={`form-control${assetErrors?.allocation?.bm30yPct || highlightAllFields ? " is-invalid" : ""}`}
              id={`${assetsType}-bm-30y-pct`}
              aria-invalid={!!(assetErrors?.allocation?.bm30yPct ?? highlightAllFields)}
              {...register(`${assetsStr}.allocation.bm30yPct`, {
                onBlur: () => {
                  void trigger(`${assetsStr}.allocation`)
                },
              })}
            />
            <span className="input-group-text">%</span>
            <InputError error={assetErrors?.allocation?.bm30yPct} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`${assetsType}-commodities-pct`} className="col-sm-3 col-form-label">
            Commodities
          </label>
          <div className="input-group">
            <input
              type="string"
              className={`form-control${assetErrors?.allocation?.commoditiesPct || highlightAllFields ? " is-invalid" : ""}`}
              id={`${assetsType}-commodities-pct`}
              aria-invalid={!!(assetErrors?.allocation?.commoditiesPct ?? highlightAllFields)}
              {...register(`${assetsStr}.allocation.commoditiesPct`, {
                onBlur: () => {
                  void trigger(`${assetsStr}.allocation`)
                },
              })}
            />
            <span className="input-group-text">%</span>
            <InputError error={assetErrors?.allocation?.commoditiesPct} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`${assetsType}-gold-pct`} className="col-sm-3 col-form-label">
            Gold
          </label>
          <div className="input-group">
            <input
              type="string"
              className={`form-control${assetErrors?.allocation?.goldPct || highlightAllFields ? " is-invalid" : ""}`}
              id={`${assetsType}-gold-pct`}
              aria-invalid={!!(assetErrors?.allocation?.goldPct ?? highlightAllFields)}
              {...register(`${assetsStr}.allocation.goldPct`, {
                onBlur: () => {
                  void trigger(`${assetsStr}.allocation`)
                },
              })}
            />
            <span className="input-group-text">%</span>
            <InputError error={assetErrors?.allocation?.goldPct} />
          </div>
        </div>
      </div>
      <fieldset
        className="form-group my-3 p-3 border rounded"
        id={`${assetsType}-fama-french-factors`}
      >
        <legend className="fs-5 d-flex align-items-center">
          Fama-French Factors
          <InfoPopover
            content="Only taken into account after July 1926. See <a href='https://earlyretirementnow.com/2019/06/12/my-thoughts-on-small-cap-and-value-stocks/' target='_blank'>here</a> for guidance."
            htmlContent={true}
          />
        </legend>
        <div className="row py-1">
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-small-stocks-pct`} className="col-form-label">
              Small Stocks (SMB)
            </label>
            <div className="input-group">
              <input
                type="string"
                className={`form-control${assetErrors?.famaFrenchFactors?.smallStocksPct ? " is-invalid" : ""}`}
                id={`${assetsType}-small-stocks-pct`}
                {...register(`${assetsStr}.famaFrenchFactors.smallStocksPct`)}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.famaFrenchFactors?.smallStocksPct} />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-value-stocks-pct`} className="col-form-label">
              Value Stocks (HML)
            </label>
            <div className="input-group">
              <input
                type="string"
                className={`form-control${assetErrors?.famaFrenchFactors?.valueStocksPct ? " is-invalid" : ""}`}
                id={`${assetsType}-value-stocks-pct`}
                {...register(`${assetsStr}.famaFrenchFactors.valueStocksPct`)}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.famaFrenchFactors?.valueStocksPct} />
            </div>
          </div>
        </div>
      </fieldset>
    </fieldset>
  )
}

export default Assets
